import React, { useEffect, useState, useMemo } from "react";
import { dms, niceDate, inr } from "./api";
import { PageHeader } from "./OwnerPages";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LogIn, LogOut, Unlock, Search, MapPin } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DayReportScreen } from "./DayPlanPages";

const getGps = () => new Promise((resolve) => {
  const via_app = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  if (!("geolocation" in navigator)) return resolve({ via_app });
  navigator.geolocation.getCurrentPosition(
    pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy, via_app }),
    () => resolve({ via_app }),
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
  );
});

const workingHrs = (p) => {
  if (!p?.in_at) return "—";
  const a = new Date(p.in_at); const b = p.out_at ? new Date(p.out_at) : new Date();
  return ((b - a) / 3600000).toFixed(2) + " h";
};

const ROLE_LABEL = { salesperson: "Salesperson", team_leader: "Team Leader", regional_manager: "Regional Manager" };

// ── Punch card (own) — works for salesperson & team leader ──
const askLocation = () => new Promise((resolve) => {
  if (!("geolocation" in navigator)) { resolve({ ok: false, code: 0 }); return; }
  navigator.geolocation.getCurrentPosition(
    () => resolve({ ok: true }),
    (err) => resolve({ ok: false, code: err && err.code }),
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
  );
});
const explainLocation = (code) => {
  if (code === 1) return "Location permission is blocked for this app. Open Chrome > 3 dots > Settings > Site settings > Location, find this site, set it to Allow, then open the app again.";
  if (code === 2) return "Phone location is OFF. Pull down the top bar, turn Location ON, then press Punch In again.";
  if (code === 3) return "Could not get your location in time. Go to an open area and try again.";
  return "This phone or browser cannot give location.";
};

function PunchCard({ role, onChange }) {
  const [today, setToday] = useState(null);
  const [canPunchIn, setCanPunchIn] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);
  const isTL = role === "team_leader";
  const refresh = () => dms.punchToday().then(d => {
    setToday(d.punch || null);
    setCanPunchIn(!!d.can_punch_in);
  }).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const doIn = async () => {
    const loc = await askLocation();
    if (!loc.ok) { toast.error(explainLocation(loc.code), { duration: 10000 }); return; }
    const g = await getGps();
    try { await (isTL ? dms.tlPunchIn(g) : dms.punchIn(g)); toast.success("Punched in"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Punch-in not allowed"); }
    refresh(); onChange && onChange();
    window.dispatchEvent(new Event("dms-punch-changed"));
  };
  const doOut = async () => {
    if (role === "salesperson") { setReportOpen(true); return; }
    const g = await getGps();
    try { await (isTL ? dms.tlPunchOut(g) : dms.punchOut(g)); toast.success("Punched out"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    refresh(); onChange && onChange();
    window.dispatchEvent(new Event("dms-punch-changed"));
  };

  const punchedIn = today?.in_at && !today?.out_at;

  // Live GPS pings now run in FieldLocationGuard (works on every page).

  return (
    <Card className="p-5 mb-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Today</div>
          <div className="text-lg font-bold text-slate-900">
            {today?.in_at ? `In at ${niceDate(today.in_at)}` : "Not punched in"}
            {today?.out_at && <span className="ml-2 text-slate-500">— Out at {niceDate(today.out_at)}</span>}
          </div>
          <div className="text-sm text-slate-600 mt-0.5">Working: <b>{workingHrs(today)}</b></div>
          {today?.out_at && today?.auto_out_reason && (
            <div className="text-sm text-rose-600 font-semibold mt-1">Auto punched out: {today.auto_out_reason}</div>
          )}
        </div>
        <div className="flex-1" />
        {!punchedIn && canPunchIn && (
          <Button onClick={doIn} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sp-punch-in"><LogIn size={16} className="mr-2" /> Punch In</Button>
        )}
        {!punchedIn && !canPunchIn && !isTL && (
          <div className="text-sm text-rose-600 font-medium">Punch-in closed for today. Ask the Owner to allow again.</div>
        )}
        {punchedIn && (
          <Button onClick={doOut} className="bg-rose-600 hover:bg-rose-700" data-testid="sp-punch-out"><LogOut size={16} className="mr-2" /> Punch Out</Button>
        )}
      </div>
      <DayReportScreen open={reportOpen} punchId={today?.id} onClose={() => setReportOpen(false)}
        onDone={() => { setReportOpen(false); refresh(); onChange && onChange(); }} />
    </Card>
  );
}

export function AttendancePage() {
  const { user } = useAuth();
  const role = user?.role;
  const isOwner = role === "owner" || role === "super_admin" || role === "owner_accountant";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    dms.attendance().then(d => setRows(d.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const doReopen = async (spId, name) => {
    try { await dms.reopenPunch(spId); toast.success(`Punch-in re-enabled for ${name}`); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(r => (r.name || "").toLowerCase().includes(term) || (ROLE_LABEL[r.role] || r.role || "").toLowerCase().includes(term));
  }, [rows, q]);

  const subtitle = role === "salesperson" ? "Your attendance history"
    : role === "team_leader" ? "Your attendance + all assigned salespersons"
    : role === "regional_manager" ? "Your attendance + Team Leaders + Salespersons"
    : role === "hr" ? "All field staff attendance"
    : "All field staff attendance — allow Punch In when needed";

  return (
    <div>
      <PageHeader title="Attendance" subtitle={subtitle} />
      {!isOwner && role !== "hr" && <PunchCard role={role} onChange={refresh} />}

      <Card className="p-3 mb-3">
        <div className="relative max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or role…" className="pl-9" data-testid="attendance-search" />
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            {role !== "salesperson" && <TableHead>Name</TableHead>}
            {role !== "salesperson" && <TableHead>Role</TableHead>}
            <TableHead>Date</TableHead>
            <TableHead>In</TableHead>
            <TableHead>Out</TableHead>
            <TableHead>Working</TableHead>
            <TableHead>Total KM</TableHead>
            {isOwner && <TableHead className="text-right">Action</TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={8} className="text-center py-6 text-slate-400">Loading…</TableCell></TableRow>}
            {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-6 text-slate-400">No attendance records</TableCell></TableRow>}
            {filtered.map((p, i) => (
              <TableRow key={p.punch_id || i} className={p.is_today ? "bg-amber-50/40" : ""}>
                {role !== "salesperson" && <TableCell className="font-medium">{p.name}</TableCell>}
                {role !== "salesperson" && <TableCell className="text-xs text-slate-500">{ROLE_LABEL[p.role] || p.role}</TableCell>}
                <TableCell>{p.date}</TableCell>
                <TableCell className="text-xs">{p.in_at ? niceDate(p.in_at) : "—"}</TableCell>
                <TableCell className="text-xs">
                  {p.out_at ? niceDate(p.out_at) : <span className="text-emerald-600">In progress…</span>}
                  {p.auto_close_reason && <div className="text-[11px] text-slate-500">{p.auto_close_reason}</div>}
                  {p.signal_gaps > 0 && (
                    <div className="text-[11px] text-amber-700" data-testid="signal-gap-badge">
                      Signal gap: {p.signal_gaps} ({p.signal_gap_minutes} min)
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-xs font-semibold">{workingHrs(p)}</TableCell>
                <TableCell className="text-xs font-semibold">{p.distance_km != null ? `${p.distance_km} km` : "—"}</TableCell>
                {isOwner && (
                  <TableCell className="text-right">
                    {role !== "salesperson" && p.role === "salesperson" && p.can_reopen && (
                      <Button size="sm" variant="outline" onClick={() => doReopen(p.user_id, p.name)} data-testid={`reopen-${p.user_id}`} className="border-amber-400 text-amber-700 hover:bg-amber-50">
                        <Unlock size={14} className="mr-1" /> Allow Punch In
                      </Button>
                    )}
                    {p.reopen_granted && <span className="text-xs text-emerald-600 font-medium">Punch-in re-enabled</span>}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ── Regional Manager: My Retailers (all under RM's TLs & SPs) ──
export function RmRetailersPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [ledgerFor, setLedgerFor] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  useEffect(() => {
    dms.rmRetailers().then(d => setRows(d.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(r => (r.name || "").toLowerCase().includes(term)
      || (r.distributor_name || "").toLowerCase().includes(term)
      || (r.phone || "").includes(term));
  }, [rows, q]);

  const openLedger = async (r) => {
    setLedgerFor(r);
    setLedgerLoading(true);
    try {
      const res = await dms.secondaryLedger(r.id);
      setLedgerData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load ledger");
      setLedgerData(null);
    }
    setLedgerLoading(false);
  };

  return (
    <div>
      <PageHeader title="My Retailers" subtitle="All retailers under your Team Leaders & Salespersons" />
      <Card className="p-3 mb-3">
        <div className="relative max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search retailer / distributor…" className="pl-9" data-testid="rm-retailer-search" />
        </div>
      </Card>
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Retailer</TableHead>
            <TableHead>Distributor</TableHead>
            <TableHead>Onboarded By</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead className="text-right">Outstanding</TableHead>
            <TableHead>Last Order</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">Loading…</TableCell></TableRow>}
            {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">No retailers found</TableCell></TableRow>}
            {filtered.map(r => (
              <TableRow key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openLedger(r)} data-testid={`rm-retailer-row-${r.id}`}>
                <TableCell className="font-medium">
                  {r.name}
                  {r.region && <div className="text-xs text-slate-400 flex items-center gap-1"><MapPin size={11} />{r.region}</div>}
                </TableCell>
                <TableCell className="text-sm">{r.distributor_name || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500">{r.onboarded_by_name || "—"}</TableCell>
                <TableCell className="text-xs">{r.phone || "—"}</TableCell>
                <TableCell className="text-right font-semibold">{inr(r.outstanding || 0)}</TableCell>
                <TableCell className="text-xs">{r.last_order_at ? niceDate(r.last_order_at) : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!ledgerFor} onOpenChange={o => { if (!o) { setLedgerFor(null); setLedgerData(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Ledger — {ledgerFor?.name}</DialogTitle></DialogHeader>
          {ledgerLoading && <div className="text-center py-6 text-slate-400 text-sm">Loading…</div>}
          {!ledgerLoading && ledgerData && (
            <div className="space-y-3">
              {(() => {
                const s = (ledgerData.summary || []).find(x => x.retailer_id === ledgerFor?.id) || { billed: 0, paid: 0, outstanding: 0 };
                return (
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-3 rounded-lg bg-slate-50 border border-slate-200"><div className="text-[10px] uppercase text-slate-500">Billed</div><div className="text-sm font-bold">{inr(s.billed)}</div></div>
                    <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200"><div className="text-[10px] uppercase text-emerald-700">Paid</div><div className="text-sm font-bold text-emerald-800">{inr(s.paid)}</div></div>
                    <div className="p-3 rounded-lg bg-rose-50 border border-rose-200"><div className="text-[10px] uppercase text-rose-700">Outstanding</div><div className="text-sm font-bold text-rose-800">{inr(s.outstanding)}</div></div>
                  </div>
                );
              })()}
              <Card className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(ledgerData.entries || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-400">No transactions</TableCell></TableRow>}
                    {(ledgerData.entries || []).map((e, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs text-slate-500">{niceDate(e.at)}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded-full text-xs border ${e.kind === "invoice" ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                            {e.kind === "invoice" ? "Sale" : e.kind === "payment" ? "Payment" : e.kind}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">{e.reference_no || "—"}</TableCell>
                        <TableCell className={`text-right font-medium ${e.kind === "invoice" ? "text-slate-900" : "text-emerald-700"}`}>{inr(e.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLedgerFor(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Owner: Retailer Login Access (per-retailer ON/OFF) ──
export function RetailerAccessPage() {
  const [rows, setRows] = useState([]);
  const [trash, setTrash] = useState([]);
  const [q, setQ] = useState("");
  const [view, setView] = useState("active");
  const [status, setStatus] = useState("all");
  const [distId, setDistId] = useState("all");
  const [dists, setDists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const load = () => {
    setLoading(true);
    Promise.all([
      dms.listRetailers({ lite: 1 }).then(d => d.data || []).catch(() => []),
      dms.listDeletedRetailers().then(d => d.data || []).catch(() => []),
    ]).then(([a, b]) => { setRows(a); setTrash(b); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    dms.listDistributors().then(d => setDists(Array.isArray(d) ? d : (d.data || []))).catch(() => setDists([]));
  }, []);
  const distName = (id) => { const d = dists.find(x => x.id === id); return d ? (d.name || d.company_name || id) : ""; };
  const toggle = async (r, enabled) => {
    try {
      await dms.setRetailerLoginAccess(r.id, enabled);
      toast.success(`${r.name}: login ${enabled ? "enabled" : "disabled"}`);
      setRows(prev => prev.map(x => x.id === r.id ? { ...x, login_enabled: enabled } : x));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeRetailer = async (r) => {
    if (!window.confirm(`Delete retailer "${r.name}"? You can restore it from the Deleted view for 30 days.`)) return;
    setBusyId(r.id);
    try {
      await dms.deleteRetailer(r.id);
      toast.success(`${r.name} deleted`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusyId(null); }
  };
  const restoreRetailer = async (r) => {
    setBusyId(r.id);
    try {
      await dms.restoreRetailer(r.id);
      toast.success(`${r.name} restored`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
    finally { setBusyId(null); }
  };
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = view === "active" ? rows : trash;
    if (distId !== "all") list = list.filter(r => r.distributor_id === distId);
    if (term) list = list.filter(r => (r.name || "").toLowerCase().includes(term) || (r.phone || "").includes(term));
    if (view === "active") {
      if (status === "enabled") list = list.filter(r => r.login_enabled);
      else if (status === "disabled") list = list.filter(r => !r.login_enabled);
      else if (status === "nologin") list = list.filter(r => !r.has_login);
    }
    return list;
  }, [rows, trash, q, view, status, distId]);
  const selCls = "h-10 rounded-md border border-slate-200 bg-white px-3 text-sm";
  return (
    <div>
      <PageHeader title="Retailer Login Access" subtitle="Enable or disable login, delete or restore retailers" />
      <Card className="p-3 mb-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search retailer..." className="pl-9" data-testid="retailer-access-search" />
          </div>
          <select className={selCls} value={distId} onChange={e => setDistId(e.target.value)} data-testid="retailer-access-distributor">
            <option value="all">All distributors</option>
            {dists.map(d => <option key={d.id} value={d.id}>{d.name || d.company_name || d.id}</option>)}
          </select>
          <select className={selCls} value={view} onChange={e => setView(e.target.value)} data-testid="retailer-access-view">
            <option value="active">Active retailers ({rows.length})</option>
            <option value="deleted">Deleted - restorable ({trash.length})</option>
          </select>
          {view === "active" && (
            <select className={selCls} value={status} onChange={e => setStatus(e.target.value)} data-testid="retailer-access-status">
              <option value="all">All status</option>
              <option value="enabled">Login enabled</option>
              <option value="disabled">Login disabled</option>
              <option value="nologin">No login account</option>
            </select>
          )}
          <span className="ml-auto text-xs text-slate-500">{filtered.length} shown</span>
        </div>
      </Card>
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Retailer</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>{view === "active" ? "Has Login" : "Deleted On / By"}</TableHead>
            {view === "active" && <TableHead className="text-right">Login Access</TableHead>}
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-400">Loading...</TableCell></TableRow>}
            {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-400">{view === "active" ? "No retailers" : "No deleted retailers"}</TableCell></TableRow>}
            {filtered.map(r => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.name}<div className="text-[11px] font-normal text-slate-500">{distName(r.distributor_id)}</div></TableCell>
                <TableCell className="text-xs">{r.phone || "-"}</TableCell>
                {view === "active" ? (
                  <>
                    <TableCell className="text-xs">{r.has_login ? <span className="text-emerald-600">Yes</span> : <span className="text-slate-400">No login account</span>}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <span className={`text-xs font-medium ${r.login_enabled ? "text-emerald-600" : "text-rose-600"}`}>{r.login_enabled ? "Enabled" : "Disabled"}</span>
                        <Switch checked={!!r.login_enabled} onCheckedChange={(v) => toggle(r, v)} disabled={!r.has_login} data-testid={`retailer-login-toggle-${r.id}`} />
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <button type="button" disabled={busyId === r.id} onClick={() => removeRetailer(r)}
                        className="px-3 py-1 text-xs font-medium rounded-md border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                        data-testid={`retailer-delete-${r.id}`}>
                        {busyId === r.id ? "..." : "Delete"}
                      </button>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell className="text-xs text-slate-600">
                      {(r.deleted_at || "").slice(0, 10)} {r.deleted_by_name ? `/ ${r.deleted_by_name}` : ""}
                      {r.days_left != null && <span className="ml-2 text-amber-700">{r.days_left} days left</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <button type="button" disabled={busyId === r.id} onClick={() => restoreRetailer(r)}
                        className="px-3 py-1 text-xs font-medium rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                        data-testid={`retailer-restore-${r.id}`}>
                        {busyId === r.id ? "..." : "Restore"}
                      </button>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
